// ─── useArtefactoVersiones — hook shared cross-artefacto (ADR-025 ext 0661) ──────
// Encapsula carga de versiones + cálculo de versión activa + carga de etapa multi-etapa
// + creación de borrador. Recibe un ArtefactoAdapter genérico.
//
// Comportamiento:
//   - cargarVersiones(): GET adapter.endpointListVersiones → setTodasVersiones + calcular
//     versión activa (la más reciente con estado en {BORRADOR, EN_REVISION, APROBADO,
//     PUBLICADO, RECHAZADO}). Si tieneVersionado=false, NO calcula versión activa.
//   - cargarEtapaActual: solo si versión activa en EN_REVISION.
//   - crearBorrador(descripcion): POST adapter.endpointCrearBorrador con payload del adapter.
//
// Recarga automática al cambiar enabled/adapter.

import { useCallback, useEffect, useState } from 'react';
import { request } from '../services/api';
import type {
  ArtefactoAdapter,
  ArtefactoEtapaActual,
  ArtefactoVersion,
} from '../components/ArtefactoWorkflowBar';
import type { EstadoWorkflow } from '../components/WorkflowBar';

// 0670 — la "versión activa" para el workflow bar es solo BORRADOR/EN_REVISION/APROBADO
// + la PUBLICADO vigente. RECHAZADO y DESCARTADO ya NO se consideran "activa".
// Cuando la última versión más reciente queda en estado terminal-no-publicado
// (RECHAZADO/DESCARTADO), el hook cae a la última PUBLICADO como "vigente visible".
const ESTADOS_ACTIVOS: EstadoWorkflow[] = ['BORRADOR', 'EN_REVISION', 'APROBADO', 'PUBLICADO'];

export interface UseArtefactoVersionesOpts {
  enabled?: boolean;
  /** Formateador del mensaje de error para `alert()` al fallar `crearBorrador`. Inyectado por el componente para soportar i18n. */
  formatCrearBorradorError?: (msg: string) => string;
  /**
   * 0670 — Si true, el hook fuerza `?soloVigente=true` en el listado y solo
   * carga la última PUBLICADO (lectura). Suprimir crearBorrador y WorkflowBar
   * desde el componente padre.
   */
  soloVigente?: boolean;
}

export interface UseArtefactoVersionesResult {
  version: ArtefactoVersion | null;
  todasVersiones: ArtefactoVersion[];
  etapaActual: ArtefactoEtapaActual | null;
  loading: boolean;
  creating: boolean;
  /**
   * 0670 — true cuando la versión activa visible es la última PUBLICADO
   * porque la edición más reciente quedó RECHAZADA/DESCARTADA. Permite al
   * componente padre mostrar etiqueta "última edición rechazada".
   */
  mostrandoVigentePorFallback: boolean;
  cargarVersiones: () => Promise<void>;
  crearBorrador: (descripcion: string) => Promise<void>;
}

export function useArtefactoVersiones(
  adapter: ArtefactoAdapter,
  opts: UseArtefactoVersionesOpts = {},
): UseArtefactoVersionesResult {
  const enabled = opts.enabled ?? true;
  const soloVigente = opts.soloVigente === true;
  const [version, setVersion] = useState<ArtefactoVersion | null>(null);
  const [todasVersiones, setTodasVersiones] = useState<ArtefactoVersion[]>([]);
  const [etapaActual, setEtapaActual] = useState<ArtefactoEtapaActual | null>(null);
  const [mostrandoVigentePorFallback, setMostrandoVigentePorFallback] = useState(false);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const cargarVersiones = useCallback(async () => {
    setLoading(true);
    try {
      // 0670 — si soloVigente, añadir el query param al endpoint. Backend
      // devolverá solo la PUBLICADO.
      const endpoint = soloVigente
        ? adapter.endpointListVersiones +
          (adapter.endpointListVersiones.includes('?') ? '&' : '?') +
          'soloVigente=true'
        : adapter.endpointListVersiones;
      const res = await request<{
        data: ArtefactoVersion[];
        meta?: { total: number };
      }>(endpoint);
      const todas = res.data ?? [];
      setTodasVersiones(todas);

      // Si artefacto NO tiene versionado clásico, no calculamos versión activa ni etapa
      if (adapter.tieneVersionado === false) {
        setVersion(null);
        setEtapaActual(null);
        setMostrandoVigentePorFallback(false);
        return;
      }

      // 0670 — Versión activa con fallback:
      //   1. Si hay una versión BORRADOR/EN_REVISION/APROBADO más reciente
      //      que la última PUBLICADO → es la activa (workflow operativo).
      //   2. Si la más reciente está RECHAZADA/DESCARTADA → caer a la última
      //      PUBLICADO como vigente visible (flag fallback=true).
      //   3. Si no hay nada activo y nada publicado → null.
      const ordenadas = [...todas].sort(
        (a, b) => new Date(b.creadoEn).getTime() - new Date(a.creadoEn).getTime(),
      );
      const masReciente = ordenadas[0] ?? null;
      const masRecienteActiva =
        masReciente && ESTADOS_ACTIVOS.includes(masReciente.estado as EstadoWorkflow)
          ? masReciente
          : null;
      const ultimaPublicada = ordenadas.find((v) => v.estado === 'PUBLICADO') ?? null;

      let activa: ArtefactoVersion | null;
      let fallback = false;
      if (masRecienteActiva) {
        activa = masRecienteActiva;
      } else if (ultimaPublicada) {
        activa = ultimaPublicada;
        // Solo marcamos fallback si la razón es que la más reciente fue rechazada/descartada
        // (no si simplemente no había versiones más nuevas).
        if (
          masReciente &&
          (masReciente.estado === 'RECHAZADO' || masReciente.estado === 'DESCARTADO')
        ) {
          fallback = true;
        }
      } else {
        activa = null;
      }
      setVersion(activa);
      setMostrandoVigentePorFallback(fallback);

      // Etapa actual solo si EN_REVISION
      if (activa && activa.estado === 'EN_REVISION') {
        try {
          const rEtapa = await request<{
            data: ArtefactoEtapaActual | null;
          }>(adapter.buildEndpointEtapaActual(activa.id));
          setEtapaActual(rEtapa.data);
        } catch {
          setEtapaActual(null);
        }
      } else {
        setEtapaActual(null);
      }
    } catch {
      setVersion(null);
      setTodasVersiones([]);
      setEtapaActual(null);
      setMostrandoVigentePorFallback(false);
    } finally {
      setLoading(false);
    }
  }, [adapter, soloVigente]);

  const crearBorrador = useCallback(
    async (descripcion: string) => {
      if (!adapter.endpointCrearBorrador) {
        // Adapter no soporta crear borrador (ej: flujograma sin versionado)
        return;
      }
      setCreating(true);
      try {
        await request(adapter.endpointCrearBorrador, {
          method: 'POST',
          body: JSON.stringify(adapter.buildCrearBorradorPayload(descripcion)),
        });
        await cargarVersiones();
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        const formatted = opts.formatCrearBorradorError
          ? opts.formatCrearBorradorError(msg)
          : `Error creando borrador: ${msg}`;
        // eslint-disable-next-line no-alert
        alert(formatted);
      } finally {
        setCreating(false);
      }
    },
    [adapter, cargarVersiones],
  );

  useEffect(() => {
    if (enabled) cargarVersiones();
  }, [enabled, cargarVersiones]);

  return {
    version,
    todasVersiones,
    etapaActual,
    loading,
    creating,
    mostrandoVigentePorFallback,
    cargarVersiones,
    crearBorrador,
  };
}
